import { ChangeEvent, FocusEvent, KeyboardEvent, RefObject, useEffect, useImperativeHandle, useRef } from 'react'

import { useDelayedHandler } from 'aem-libs-react-ts/src/hooks/useDelayedHandler'
import { useInputGaTracking } from 'aem-libs-react-ts/src/hooks/useInputGaTracking'
import { conditionalInvoke } from 'aem-libs-react-ts/src/utils/conditionalInvoke'
import { Maybe } from 'aem-libs-ts/src/types/utility'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { numberFormat } from 'aem-libs-ts/src/utils/formatting/numbers/numberFormat'
import { removeNumberFormat } from 'aem-libs-ts/src/utils/formatting/numbers/removeNumberFormat'
import { clamp } from 'aem-libs-ts/src/utils/number/clamp'

import { HandlersType, TrackingConfig } from '../types'
import { NumberFieldProps } from './NumberField'


type NumberHandlers = Omit<HandlersType<number, HTMLInputElement>, 'onChange'> & {
  onChange: Maybe<(value: number) => void>
  onFocus: Maybe<(e: FocusEvent<HTMLInputElement>) => void>
}

type UseNumberFieldOptionsType = Pick<
  NumberFieldProps,
  'min' | 'max' | 'format' | 'useDelayedChange' | 'enableNull' | 'ref'
>

const enabledKeys = ['Enter', 'Backspace', 'ArrowRight', 'ArrowLeft', 'Tab', 'NumpadEnter', 'Delete']

export const useNumberField = (
  value: number | null,
  options: UseNumberFieldOptionsType,
  handlers: NumberHandlers,
  tracking: TrackingConfig
) => {
  const { trackInputChange } = useInputGaTracking({
    enableGaTracking: true,
    description: tracking?.description,
    id: tracking?.id
  })

  const { setDelayedArgs } = useDelayedHandler(handlers.onChangeComplete)

  const inputRef: RefObject<HTMLInputElement | null> = useRef(null)
  useImperativeHandle(options.ref, () => ({
    setValue: (value: number) => {
      setInputRefValue(formatValue(value))
    }
  }))

  const setInputRefValue = (value: string | null) => {
    if (inputRef.current) {
      inputRef.current.setAttribute('value', value ?? '')
      inputRef.current.value = value ?? ''
    }
  }

  const formatValue = (value: number | null) => {
    return options.format ? options.format(value ?? '') : numberFormat(value ?? '')
  }

  useEffect(() => {
    if (parseInt(removeNumberFormat(inputRef.current?.value ?? '')) !== value) {
      setInputRefValue(formatValue(value))
    }
  }, [value, inputRef.current?.value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputRefValue(e.target.value)
    conditionalInvoke(handlers.onChange, [])
    if (options && options.useDelayedChange) {
      delayedChange(e.target.value)
    }
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    valueChangeProcess(inputRef.current?.value ?? '')
    conditionalInvoke(handlers.onBlur, [e])
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setInputRefValue(removeNumberFormat(inputRef.current?.value ?? ''))
    e.target?.setSelectionRange(0, inputRef.current?.value.length ?? 0)
    conditionalInvoke(handlers.onFocus, [e])
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    conditionalInvoke(handlers.onKeyPress, [e])
    if (
      e.key.match(/[0-9]/) ||
      enabledKeys.includes(e.code) ||
      (e.code === 'KeyV' && e.metaKey) ||
      (e.code === 'KeyV' && e.ctrlKey)
    ) {
      return
    }
    e.preventDefault()
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    conditionalInvoke(handlers.onKeyPress, [e])
  }

  const delayedChange = (value: string) => {
    const tempValue = removeNumberFormat(value)
    if (options.enableNull && tempValue?.length === 0) {
      return
    }

    const numValue = +tempValue
    if (
      !isNaN(numValue) &&
      isDefined(options.min) &&
      isDefined(options.max) &&
      numValue >= options?.min &&
      numValue <= options?.max
    ) {
      setDelayedArgs(numValue)
      conditionalInvoke(handlers.onChange, [numValue])
    }
  }

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === '13') {
      inputRef.current?.blur()
    }
  }

  const valueChangeProcess = (value: string) => {
    const tempValue = removeNumberFormat(value)

    if (options.enableNull && tempValue?.length === 0) {
      conditionalInvoke(handlers.onChangeComplete, [null as unknown as number])
      return
    }

    const validatedValue = clamp(+tempValue, options.min, options.max) as number
    trackInputChange(validatedValue as number)

    if (inputRef.current) {
      inputRef.current.value = formatValue(validatedValue)
    }
    conditionalInvoke(handlers.onChangeComplete, [validatedValue])
  }

  return { inputRef, handleKeyPress, handleFocus, handleBlur, handleChange, handleKeyDown, handleKeyUp }
}
